import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { DollarSign, ShoppingBag, Users, Package, AlertTriangle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topClientsYear, setTopClientsYear] = useState([]);
  const [topSubscriptionClients, setTopSubscriptionClients] = useState([]);

  useEffect(() => {
    fetchDashboardStats();
    fetchTopClientsYear();
    fetchTopSubscriptionClients();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/reports/dashboard');
      setStats(response.data);
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchTopClientsYear = async () => {
    try {
      const response = await axios.get('/reports/top-clients-year?limit=5');
      setTopClientsYear(response.data);
    } catch (error) {
      console.error('Error al cargar mejores clientes');
    }
  };

  const fetchTopSubscriptionClients = async () => {
    try {
      const response = await axios.get('/reports/top-subscription-clients-year?limit=5');
      setTopSubscriptionClients(response.data);
    } catch (error) {
      console.error('Error al cargar clientes por suscripción');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Ingresos Hoy',
      value: `$${stats?.today_income?.toFixed(2) || '0.00'}`,
      subtitle: `${stats?.today_sales_count || 0} ventas`,
      icon: DollarSign,
      color: '#10b981',
      bgColor: '#d1fae5',
    },
    {
      title: 'Suscripciones Activas',
      value: stats?.active_subscriptions || 0,
      subtitle: `${stats?.expiring_subscriptions || 0} por vencer`,
      icon: Users,
      color: '#3b82f6',
      bgColor: '#dbeafe',
    },
    {
      title: 'Total Productos',
      value: stats?.total_products || 0,
      subtitle: `${stats?.low_stock_products || 0} stock bajo`,
      icon: Package,
      color: '#f97316',
      bgColor: '#fed7aa',
    },
    {
      title: 'Total Clientes',
      value: stats?.total_clients || 0,
      subtitle: 'Clientes activos',
      icon: TrendingUp,
      color: '#8b5cf6',
      bgColor: '#ede9fe',
    },
  ];

  const alerts = [];
  
  if (stats?.low_stock_products > 0) {
    alerts.push({
      type: 'warning',
      message: `${stats.low_stock_products} productos con stock bajo`,
    });
  }

  if (stats?.expiring_subscriptions > 0) {
    alerts.push({
      type: 'info',
      message: `${stats.expiring_subscriptions} suscripciones por vencer`,
    });
  }

  if (stats?.expired_subscriptions > 0) {
    alerts.push({
      type: 'error',
      message: `${stats.expired_subscriptions} suscripciones vencidas`,
    });
  }

  return (
    <div className="dashboard" data-testid="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Resumen general del negocio</p>
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="stat-card" data-testid={`stat-card-${index}`}>
              <div className="stat-card-content">
                <div className="stat-info">
                  <p className="stat-title">{card.title}</p>
                  <h2 className="stat-value">{card.value}</h2>
                  <p className="stat-subtitle">{card.subtitle}</p>
                </div>
                <div 
                  className="stat-icon"
                  style={{ background: card.bgColor }}
                >
                  <Icon size={24} style={{ color: card.color }} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {alerts.length > 0 && (
        <div className="alerts-section">
          <h2 className="section-title">Alertas</h2>
          <div className="alerts-grid">
            {alerts.map((alert, index) => (
              <Card key={index} className={`alert-card alert-${alert.type}`} data-testid={`alert-${index}`}>
                <AlertTriangle size={20} />
                <span>{alert.message}</span>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* New Client Cards */}
      <div className="clients-section">
        <div className="clients-grid">
          {/* Top Clients by Sales */}
          <Card className="client-ranking-card">
            <div className="card-header">
              <h3 className="card-title">
                <TrendingUp size={20} />
                Mejores Clientes {new Date().getFullYear()}
              </h3>
              <p className="card-subtitle">Por monto total de ventas</p>
            </div>
            {topClientsYear.length === 0 ? (
              <div className="empty-state">No hay datos disponibles</div>
            ) : (
              <div className="ranking-list">
                {topClientsYear.map((client, index) => (
                  <div key={index} className="ranking-item">
                    <div className="rank-number">{index + 1}</div>
                    <div className="client-info">
                      <div className="client-name">{client.cliente_nombre}</div>
                      <div className="client-count">{client.sales_count} ventas</div>
                    </div>
                    <div className="client-amount">${client.total_amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Top Clients by Subscriptions */}
          <Card className="client-ranking-card">
            <div className="card-header">
              <h3 className="card-title">
                <Users size={20} />
                Clientes por Suscripción {new Date().getFullYear()}
              </h3>
              <p className="card-subtitle">Por valor de suscripciones</p>
            </div>
            {topSubscriptionClients.length === 0 ? (
              <div className="empty-state">No hay datos disponibles</div>
            ) : (
              <div className="ranking-list">
                {topSubscriptionClients.map((client, index) => (
                  <div key={index} className="ranking-item">
                    <div className="rank-number">{index + 1}</div>
                    <div className="client-info">
                      <div className="client-name">{client.cliente_nombre}</div>
                      <div className="client-count">{client.subscriptions_count} suscripciones</div>
                    </div>
                    <div className="client-amount">${client.total_amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      <style jsx>{`
        .dashboard {
          max-width: 1400px;
        }

        .dashboard-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        }

        .dashboard-header {
          margin-bottom: 2rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
          font-size: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          padding: 1.5rem;
          transition: all 0.3s;
        }

        .stat-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .stat-card-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .stat-info {
          flex: 1;
        }

        .stat-title {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .stat-subtitle {
          font-size: 0.875rem;
          color: #64748b;
        }

        .stat-icon {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .section-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1rem;
        }

        .alerts-section {
          margin-top: 2rem;
        }

        .alerts-grid {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .alert-card {
          padding: 1rem 1.25rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-left: 4px solid;
          font-weight: 500;
        }

        .alert-warning {
          border-left-color: #f59e0b;
          background: #fffbeb;
          color: #92400e;
        }

        .alert-info {
          border-left-color: #3b82f6;
          background: #eff6ff;
          color: #1e40af;
        }

        .alert-error {
          border-left-color: #ef4444;
          background: #fef2f2;
          color: #991b1b;
        }

        /* Client Ranking Cards */
        .clients-section {
          margin-top: 2rem;
        }

        .clients-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .client-ranking-card {
          padding: 1.5rem;
        }

        .card-header {
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .card-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .card-subtitle {
          font-size: 0.875rem;
          color: #64748b;
          margin: 0;
        }

        .ranking-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 8px;
          transition: transform 0.2s;
        }

        .ranking-item:hover {
          transform: translateX(4px);
          background: #f1f5f9;
        }

        .rank-number {
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.875rem;
          flex-shrink: 0;
        }

        .client-info {
          flex: 1;
          min-width: 0;
        }

        .client-name {
          font-weight: 600;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .client-count {
          font-size: 0.875rem;
          color: #64748b;
        }

        .client-amount {
          font-size: 1.125rem;
          font-weight: 700;
          color: #10b981;
          flex-shrink: 0;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .page-title {
            font-size: 1.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .clients-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
